import { Component, Directive, Inject, OnInit } from '@angular/core';
import { SharedModule } from '@Shared/shared.module';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import moment from 'moment';
import { BaseFormFieldServices, BaseInterviewFormFieldComponent } from '../base-interview-form-field/base-interview-form-field.component';

import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MAT_MOMENT_DATE_FORMATS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { Moment } from 'moment';
import { MatDatepicker } from '@angular/material/datepicker';
import { FeatureToggle } from '@Enums';
import { FieldDetailsComponent } from '@Components/field-details/field-details.component';
import { MaskDate2Directive, MaskDateDirective } from '@App/directives';
import { InterviewProcessingStatusComponent } from '@App/interview/components/interview-processing-status/interview-processing-status.component';

@Component({
    selector: 'short-date-answer',
    templateUrl: './short-date-answer.component.html',
    styleUrls: ['./short-date-answer.component.scss'],
    providers: [
        { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
        { provide: MAT_DATE_FORMATS, useValue: MAT_MOMENT_DATE_FORMATS },
        { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
        {
            provide: MAT_DATE_FORMATS,
            useValue: {
                parse: {
                    dateInput: ['YYYY-MM', 'MM/YYYY'],
                },
                display: {
                    dateInput: 'MM/YYYY',
                    monthYearLabel: 'MMM YYYY',
                    dateA11yLabel: 'LL',
                    monthYearA11yLabel: 'MMMM YYYY',
                }
            }
        }
    ],
    imports: [SharedModule, FieldDetailsComponent, MaskDate2Directive, MaskDateDirective, InterviewProcessingStatusComponent]
})
export class ShortDateAnswerComponent extends BaseInterviewFormFieldComponent implements OnInit {
    maxDate = '';
    minDate = '';
    maxYear = '';
    minYear = '';
    pickerFieldName: string;
    forcedFieldName: string;
    allowDateFormatSelection = false;

    constructor(
        private fb: UntypedFormBuilder,
        public services: BaseFormFieldServices) {

        super(services);
    }

    ngOnInit() {

        this.allowDateFormatSelection = this.services.featureService.getByName(FeatureToggle.ConfigInterviewAllowDateFormat).enabled;

        if (this.config.DateRangeType) {

            if (this.config.DateRangeType === 'absolute') {
                // if dateRangetype==absolute, set mindate and maxdate validations directly from config.
                if (this.config.MaxDate) {
                    this.maxDate = this.config.MaxDate;
                }
                if (this.config.MinDate) {
                    this.minDate = this.config.MinDate;
                }
                if (this.config.MaxYear) {
                    this.maxYear = this.config.MaxYear;
                }
                if (this.config.MinYear) {
                    this.minYear = this.config.MinYear;
                }
            } else if (this.config.DateRangeType === 'relative') {
                // if daterangetype is 'relative', calculate mindate and maxdate validations using prior and later values from config and calculate it from current date.
                if (this.config.MinDateType && this.config.MinNoOfDays != null) {
                    const currMinDate = new Date(new Date().toISOString().split('T')[0]);

                    if (this.config.MinDateType === 'priorDays') {
                        currMinDate.setDate(currMinDate.getDate() - parseInt(this.config.MinNoOfDays));
                    } else if (this.config.MinDateType === 'laterDays') {
                        currMinDate.setDate(currMinDate.getDate() + parseInt(this.config.MinNoOfDays));
                    }

                    this.minDate = currMinDate.toISOString().split('T')[0];
                }
                if (this.config.MaxDateType && this.config.MaxNoOfDays != null) {
                    const currMaxDate = new Date(new Date().toISOString().split('T')[0]);

                    if (this.config.MaxDateType === 'priorDays') {
                        currMaxDate.setDate(currMaxDate.getDate() - parseInt(this.config.MaxNoOfDays));
                    } else if (this.config.MaxDateType === 'laterDays') {
                        currMaxDate.setDate(currMaxDate.getDate() + parseInt(this.config.MaxNoOfDays));
                    }

                    this.maxDate = currMaxDate.toISOString().split('T')[0];
                }

                if (this.config.MinDateType && this.config.MinNoOfYears != null) {
                    const currMinYear = new Date(new Date().toISOString().split('T')[0]);

                    if (this.config.MinDateType === 'priorYears') {
                        currMinYear.setFullYear(currMinYear.getFullYear() - parseInt(this.config.MinNoOfYears));
                    } else if (this.config.MinDateType === 'laterYears') {
                        currMinYear.setFullYear(currMinYear.getFullYear() + parseInt(this.config.MinNoOfYears));
                    }

                    this.minDate = currMinYear.toISOString().split('T')[0];
                }
                if (this.config.MaxDateType && this.config.MaxNoOfYears != null) {
                    const currMaxYear = new Date(new Date().toISOString().split('T')[0]);

                    if (this.config.MaxDateType === 'priorYears') {
                        currMaxYear.setFullYear(currMaxYear.getFullYear() - parseInt(this.config.MaxNoOfYears));
                    } else if (this.config.MaxDateType === 'laterYears') {
                        currMaxYear.setFullYear(currMaxYear.getFullYear() + parseInt(this.config.MaxNoOfYears));
                    }

                    this.maxDate = currMaxYear.toISOString().split('T')[0];
                }
            }
        }

        this.pickerFieldName = `${this.config.id}_picker`;
        this.forcedFieldName = `${this.config.id}_forceUpdate`;

        this.group.addControl(this.pickerFieldName, this.fb.control(''));
        this.group.addControl(this.forcedFieldName, this.fb.control(''));
        const pickerField = this.group.get(this.pickerFieldName);
        if (this.config.isRequired) {
            pickerField.setValidators(Validators.required);
        }

        const existingValue = this.group.get(this.config.id).value;
        if (existingValue != null && existingValue !== '') {
            pickerField.setValue(moment.utc(existingValue));
        }

        super.initialize({ inputType: 'text' });
        this.subscriptions.push(this.group.get(this.forcedFieldName).valueChanges.subscribe(
            data => {
                const forcedControl = this.group.get(this.forcedFieldName);
                const pickerControl = this.group.get(this.pickerFieldName);
                if (forcedControl != null && pickerControl?.value !== forcedControl.value) {
                    pickerControl.setValue(forcedControl.value);
                }
                this.onDateChange();
            }
        ));
    }

    setMonthAndYear(monthYear: Moment, datepicker: MatDatepicker<any>) {
        const pickerControl = this.group.get(this.pickerFieldName);
        pickerControl.setValue(monthYear.format('yyyy-MM'));

        this.onDateChange();
        datepicker.close();
    }

    // on blur of the field, validate the field manually as Validations.minDate and maxDate doesnt work on clicking the date popup calendar.
    onDateChange(_event?: FocusEvent) {
        this.services.featureService?.isEnabled(FeatureToggle.CaseInterviewDatePickerToggle).subscribe(isEnabled => {
            if (this.isLiveMode()) {
                const pickerControl = this.group.get(this.pickerFieldName);
                const fieldControl = this.group.get(this.config.id);

                const dateFormat = 'YYYY-MM';
                const pickerValue = pickerControl.value == null || pickerControl.value === '' ?
                    null
                    : moment(pickerControl.value).format(dateFormat);

                fieldControl.setValue(pickerValue);
                fieldControl.markAsTouched();
                fieldControl.markAsDirty();
                fieldControl.updateValueAndValidity();

                // needed to trigger the red question block, since validation isn't actually occuring
                // on the `config.id` control.
                fieldControl.setErrors(pickerControl.errors);
            }

            if (isEnabled) {
                const dateFormat = 'YYYY-MM';
                const normalize = (v: any) => v ? moment(v).format(dateFormat) : '';
                if (normalize(this.group.get(this.config.id)?.value) !== normalize(this.previousFormFieldValues?.[this.config.id])) {
                    super.onBlur();
                }
            } else {
                super.onBlur();
            }
        });
    }
}

export const DATE_FORMAT_1 = {
    parse: {
        dateInput: ['MM-YYYY-DD', 'YYYY-MM-DD', 'MM/DD/YYYY'],
    },
    display: {
        dateInput: 'MM/DD/YYYY',
        monthYearLabel: 'MMM YYYY',
        dateA11yLabel: 'LL',
        monthYearA11yLabel: 'MMMM YYYY',
    }
};
  
export const DATE_FORMAT_2 = {
    parse: {
        dateInput: ['YYYY-MM', 'MM/YYYY', 'MM-YYYY']
    },
    display: {
        dateInput: 'MM/YYYY',
        monthYearLabel: 'MM/YYYY',
        dateA11yLabel: 'LL',
        monthYearA11yLabel: 'MMMM YYYY',
    },
};
  
  @Directive({
      selector: '[dateFormat1]',
      providers: [
          {provide: MAT_DATE_FORMATS, useValue: DATE_FORMAT_1},
      ],
  })
export class CustomDateFormat1 {
}
  
  @Directive({
      selector: '[dateFormat2]',
      providers: [
          {provide: MAT_DATE_FORMATS, useValue: DATE_FORMAT_2},
      ],
  })
export class CustomDateFormat2 {
}
